"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { ChevronLeft, ChevronRight, Code2, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { formatDate, formatDateTime, formatNumber } from "@/lib/format-datetime";
import { Skeleton } from "@/components/ui/skeleton";
import { ProgramSourceView } from "@/components/code/ProgramSourceView";
import { useAuth } from "@/components/providers/auth-provider";
import { formatRuntimeLanguageName } from "@/features/quiz/lib/coding-task-format";
import { JUDGE_VERDICTS } from "@/features/quiz/coding-task-api";
import { useJudgeProblemSubmissions, useJudgeRuntimes, useJudgeSubmissionSource } from "../../hooks";
import { VerdictChip } from "./SubmissionResult";

/**
 * The Submissions tab, `EX-SRC01` (ADR-0082, ADR-0085; the founder asked for
 * it with filters on 2026-09-26).
 *
 * Every judged practice submission on the problem, newest first, filtered by
 * everyone or mine, verdict and language. The list never carries a program;
 * a row the server marks `canRead` opens it in a panel under the table. Who
 * may read what is the server's answer, row by row, so a line above the table
 * only explains it: sign in, then solve it, and other people's accepted
 * programs open.
 */
const LIST_VERDICTS = JUDGE_VERDICTS.filter((verdict) => verdict !== "internal_error" && verdict !== "skipped");
const PAGE_SIZE = 20;

function kb(memoryKb: number | null, locale: string): string {
  if (memoryKb === null) return "";
  return memoryKb < 1024
    ? `${formatNumber(memoryKb, locale)} KB`
    : `${formatNumber(memoryKb / 1024, locale, { maximumFractionDigits: 1 })} MB`;
}

function FilterSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 rounded-lg border border-neutral-200 bg-white px-2.5 text-sm font-medium text-neutral-800"
    >
      {children}
    </select>
  );
}

function ProgramPanel({ slug, submissionId, locale }: { slug: string; submissionId: string; locale: string }) {
  const t = useTranslations("judge.problem");
  const query = useJudgeSubmissionSource(slug, submissionId);

  if (query.isPending) return <Skeleton className="h-40" />;
  if (query.isError) {
    const solveFirst = query.error instanceof ApiError && query.error.status === 403;
    return <p className="text-sm text-red-700">{t(solveFirst ? "submissionsSolveFirst" : "submissionsSourceFailed")}</p>;
  }
  const program = query.data;
  return (
    <div className="space-y-2">
      <p className="text-sm text-neutral-700">
        {t("submissionsProgramBy", {
          name: program.name || program.username || "",
          when: formatDateTime(program.submittedAt, locale),
        })}
      </p>
      <ProgramSourceView
        source={program.sourceCode}
        languageKey={program.languageKey}
        languageName={formatRuntimeLanguageName(program.languageKey)}
        languageVersion={program.languageVersion}
        labels={{
          lines: t("sourceLines", { count: program.sourceCode.split("\n").length }),
          wrap: t("sourceWrap"),
          copy: t("sourceCopy"),
          copied: t("sourceCopied"),
        }}
      />
    </div>
  );
}

export function SubmissionsTab({
  slug,
  locale,
  active,
  openProgramId,
}: {
  slug: string;
  locale: string;
  active: boolean;
  /** A program asked for from another tab (the statistics lists). */
  openProgramId?: string | null;
}) {
  const t = useTranslations("judge.problem");
  const tJudge = useTranslations("judge");
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const viewerKey = authLoading ? "loading" : isAuthenticated ? "member" : "anon";
  const runtimes = useJudgeRuntimes(active);
  const signInHref = useSignInHref(locale);

  const [scope, setScope] = useState<"all" | "mine">("all");
  const [verdict, setVerdict] = useState("");
  const [language, setLanguage] = useState("");
  const [page, setPage] = useState(1);
  const [programId, setProgramId] = useState<string | null>(openProgramId ?? null);

  useEffect(() => {
    if (openProgramId) setProgramId(openProgramId);
  }, [openProgramId]);

  const query = useJudgeProblemSubmissions(
    slug,
    {
      page,
      limit: PAGE_SIZE,
      ...(verdict ? { verdict } : {}),
      ...(language ? { language } : {}),
      ...(scope === "mine" ? { mine: true } : {}),
    },
    viewerKey,
    active && !authLoading,
  );

  const reset = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setPage(1);
  };

  const data = query.data;
  const viewer = data?.viewer;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {isAuthenticated ? (
          <div role="group" aria-label={t("submissionsScope")} className="inline-flex rounded-lg bg-neutral-100 p-0.5">
            {(["all", "mine"] as const).map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={scope === key}
                onClick={() => reset(setScope)(key)}
                className={cn(
                  "min-h-9 rounded-md px-3 text-xs font-semibold transition-colors duration-150 ease-out",
                  scope === key ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500 hover:text-neutral-900",
                )}
              >
                {t(key === "all" ? "submissionsEveryone" : "submissionsMine")}
              </button>
            ))}
          </div>
        ) : null}
        <FilterSelect label={t("submissionsVerdictFilter")} value={verdict} onChange={reset(setVerdict)}>
          <option value="">{t("submissionsAnyVerdict")}</option>
          {LIST_VERDICTS.map((key) => (
            <option key={key} value={key}>
              {tJudge(`verdict.${key}`)}
            </option>
          ))}
        </FilterSelect>
        {(runtimes.data?.languages.length ?? 0) > 0 ? (
          <FilterSelect label={t("submissionsLanguageFilter")} value={language} onChange={reset(setLanguage)}>
            <option value="">{t("submissionsAnyLanguage")}</option>
            {runtimes.data!.languages.map((lang) => (
              <option key={lang.id} value={lang.id}>
                {lang.name}
              </option>
            ))}
          </FilterSelect>
        ) : null}
      </div>

      {viewer && !viewer.solved ? (
        <p className="flex items-start gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
          <span>
            {viewer.signedIn ? (
              t("submissionsLockSolve")
            ) : (
              <>
                {t("submissionsLockSignIn")}{" "}
                <Link href={signInHref} className="font-semibold text-primary hover:underline">
                  {t("submissionsSignIn")}
                </Link>
              </>
            )}
          </span>
        </p>
      ) : null}

      {query.isPending ? (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} className="h-10" />
          ))}
        </div>
      ) : query.isError ? (
        <p className="text-sm text-red-700">{t("submissionsLoadFailed")}</p>
      ) : data!.items.length === 0 ? (
        <p className="text-sm text-neutral-500">{t(scope === "mine" ? "submissionsNoneMine" : "submissionsNone")}</p>
      ) : (
        // `relative` makes this box the containing block of the Code column's
        // `sr-only` heading (absolutely positioned). Without it that heading
        // escapes the sideways scroll and widens the whole page on a phone.
        <div data-testid="submissions-scroll" className="relative overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500">
                <th scope="col" className="w-full py-2 pr-3 font-medium">{t("statsWhoCol")}</th>
                <th scope="col" className="py-2 pr-3 font-medium">{t("submissionsVerdictCol")}</th>
                <th scope="col" className="py-2 pr-3 font-medium">{t("statsLanguageCol")}</th>
                <th scope="col" className="py-2 pr-3 text-right font-medium">{t("stats_fastest_col")}</th>
                <th scope="col" className="py-2 pr-3 text-right font-medium">{t("stats_lightest_col")}</th>
                <th scope="col" className="py-2 pr-3 text-right font-medium">{t("stats_shortest_col")}</th>
                <th scope="col" className="py-2 pr-3 font-medium">{t("submissionsWhenCol")}</th>
                <th scope="col" className="py-2 font-medium">
                  <span className="sr-only">{t("submissionsCodeCol")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data!.items.map((row) => (
                <tr
                  key={row.submissionId}
                  className={cn(
                    "border-b border-neutral-100 last:border-0",
                    programId === row.submissionId && "bg-neutral-50",
                  )}
                >
                  <td className="w-full max-w-0 truncate py-2 pr-3">
                    {row.username ? (
                      <Link href={`/${locale}/u/${row.username}`} className="font-medium text-neutral-900 hover:text-primary">
                        {row.name || row.username}
                      </Link>
                    ) : (
                      <span className="font-medium text-neutral-900">{row.name}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3">
                    <VerdictChip status="completed" verdict={row.verdict} />
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-neutral-700">{formatRuntimeLanguageName(row.languageKey)}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-neutral-700">
                    {row.timeMs === null ? "" : `${formatNumber(row.timeMs, locale)} ms`}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-neutral-700">{kb(row.memoryKb, locale)}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-neutral-700">
                    {`${formatNumber(row.sourceBytes, locale)} B`}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-neutral-500" title={formatDateTime(row.submittedAt, locale)}>
                    {formatDate(row.submittedAt, locale)}
                  </td>
                  <td className="py-2 text-right">
                    {row.canRead ? (
                      <button
                        type="button"
                        onClick={() => setProgramId(row.submissionId)}
                        aria-pressed={programId === row.submissionId}
                        className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-semibold text-primary hover:bg-primary/10"
                      >
                        <Code2 className="h-3.5 w-3.5" aria-hidden />
                        {t("submissionsViewCode")}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3 text-sm text-neutral-600">
          <span className="tabular-nums">
            {t("submissionsPage", { page: formatNumber(data.page, locale), pages: formatNumber(data.totalPages, locale) })}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
              aria-label={t("submissionsPrev")}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              disabled={page >= data.totalPages}
              onClick={() => setPage((value) => value + 1)}
              aria-label={t("submissionsNext")}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      ) : null}

      {programId ? <ProgramPanel slug={slug} submissionId={programId} locale={locale} /> : null}
    </div>
  );
}
